import type {
  LandingPageHero,
  LandingPageBenefits,
  LandingPageApproach,
  LandingPageWhatYouGet,
  LandingPageKlartext,
  LandingPageUnterschied,
  LandingPagePricingIntro,
  LandingPageGoDeeper,
  LandingPageFaqItem,
  LandingPageClosing,
  LandingPageWhoTrades,
  LandingPageContent,
} from './types';
import svenBolz from '@/assets/images/sven_bolz.jpg';

export const DEFAULT_SENJA_WIDGET_ID = 'b56d4a59-c12b-4a9c-97aa-1eea8e064f57';

export const DEFAULT_HERO_VIDEO_URL = 'https://iframe.mediadelivery.net/embed/491441/1b492795-3265-435f-a50d-cd59e54cc7c5?autoplay=true&captions=true&muted=true';

export const DEFAULT_HERO: LandingPageHero = {
  headline: 'Warum verlieren die meisten an der Börse Geld?',
  subheadlineLines: [
    'Nicht wegen dem Markt.',
    'Nicht wegen Krisen.',
    'Weil sie keine Struktur haben.',
  ],
  descriptionLines: [
    'Ich zeige dir hier nicht, was du denken sollst.',
    'Ich zeige dir, was und wie ich entscheide.',
  ],
  ctaLabel: 'Ich will klare Entscheidungen',
};

export const DEFAULT_BENEFITS: LandingPageBenefits = {
  headline: 'Die meisten bleiben genau hier hängen…',
  painPoints: [
    'sie kaufen, wenn es positive Nachrichten gibt',
    'sie verkaufen, wenn es weh tut',
    'sie springen rein und raus',
    'sie haben keinen klaren Plan',
  ],
  conclusion: '...und wundern sich dann, warum nichts funktioniert.',
  punchline: 'Die Börse ist nicht das Problem – deine Entscheidungen sind es!',
};

export const DEFAULT_APPROACH: LandingPageApproach = {
  philosophyLines: [
    'Ich arbeite nicht mit Meinungen.',
    'Ich arbeite nicht mit Nachrichten.',
  ],
  finalLine: 'Ich arbeite mit Charts und menschlichem Verhalten.',
  bridgeLine: 'Das bedeutet:',
  bulletItems: [
    'Wann zeigen gewisse Aktien Stärke?',
    'Wann zeigt die Börse Schwäche?',
    'Wann macht ein Einstieg Sinn – und wann nicht?',
  ],
};

export const DEFAULT_WHAT_YOU_GET: LandingPageWhatYouGet = {
  headline: 'Was du hier bekommst',
  subheadline: 'Keine Theorie – sondern gelebte Praxis',
  highlight: 'Meine Entscheidungen im aktuellen Markt, klare Einstiege und nachvollziehbares Trade-Management.',
};

export const DEFAULT_KLARTEXT: LandingPageKlartext = {
  title: 'Klartext',
  notForYouHeader: 'Das hier ist nichts für dich, wenn',
  notForYou: 'du schnelle Tipps suchst, keine Lust hast umzusetzen oder nur „mal schauen willst".',
  forYouHeader: 'Das hier ist für dich, wenn',
  forYouLines: [
    'du endlich Klarheit willst, du aufhören willst, hinterherzurennen, und du verstehen willst, was du tust.',
  ],
};

export const DEFAULT_UNTERSCHIED: LandingPageUnterschied = {
  title: 'Der Unterschied',
  theyHeader: 'Die meisten schauen auf',
  theyItems: ['Nachrichten', 'Meinungen', 'Schlagzeilen'],
  meHeader: 'Ich schaue auf',
  meItems: ['Verhalten', 'Struktur', 'Bewegung im Markt'],
};

export const DEFAULT_PRICING_INTRO: LandingPagePricingIntro = {
  mainHeadline: 'Und das ändert extrem viel:',
  beforeHeader: 'Du hörst auf:',
  beforeItems: [
    'planlos zu investieren',
    'emotional zu reagieren',
    'Trends hinterherzulaufen',
  ],
  afterHeader: 'Und fängst an:',
  afterItems: [
    'strukturiert zu handeln',
    'klar zu entscheiden',
    'Chancen bewusst zu nutzen',
  ],
};

export const DEFAULT_GO_DEEPER: LandingPageGoDeeper = {
  headline: 'Wenn du tiefer gehen willst',
  body: 'Wenn du das Ganze wirklich verstehen und selbst umsetzen willst, gibt es den nächsten Schritt mit der Ausbildung und Community.',
  ctaLabel: 'Mehr zur 100K Community',
};

export const DEFAULT_FAQ_ITEMS: LandingPageFaqItem[] = [
/*  {
    question: 'Was kostet der Börsencampus?',
    answer: 'Der Börsencampus bietet vier Pakete ab {cheapestPrice} € / Monat. Je nach Paket erhältst du Zugang zu langfristigen Investmentideen, Swing-Trades, Cashflow-Strategien oder Live-Daytrading-Sessions. Bei längerer Laufzeit sparst du zusätzlich.',
  },*/
  {
    question: 'Für wen ist der Börsencampus geeignet?',
    answer: 'Der Börsencampus richtet sich an alle, die an der Börse aktiv sind oder es werden wollen – vom Einsteiger bis zum erfahrenen Trader. Unsere Inhalte reichen von langfristigen Investmentideen bis hin zu täglichen Daytrading-Sessions.',
  },
  {
    question: 'Kann ich jederzeit kündigen?',
    answer: 'Ja, du kannst dein Paket jederzeit zum Ende der Laufzeit kündigen. Es gibt keine versteckten Kosten und keine Mindestvertragslaufzeit über die gewählte Abrechnungsperiode hinaus.',
  },
  {
    question: 'Was unterscheidet den Börsencampus von anderen Angeboten?',
    answer: 'Im Börsencampus bekommst du nicht nur Tradingideen, sondern auch die Erklärung dahinter. Transparente Ergebnisse, echte Trades und eine bewertete Community mit 4,9/5 Sternen auf ProvenExpert sprechen für sich.',
  },
  {
    question: 'Wie erhalte ich die Tradingideen?',
    answer: 'Nach der Registrierung hast du sofort Zugang zum Campus-Dashboard. Neue Tradingideen, Analysen und Markteinschätzungen werden direkt in deinem Dashboard angezeigt.',
  },
];

export const DEFAULT_CLOSING: LandingPageClosing = {
  sloganLines: [
    { text: 'Investieren baut', highlight: 'Vermögen!' },
    { text: 'Cashflow bezahlt dein', highlight: 'Leben!' },
  ],
  ctaLabel: 'Jetzt Zugang sichern',
};

export const DEFAULT_WHO_TRADES: LandingPageWhoTrades = {
  headline: 'Wer da eigentlich handelt',
  paragraphs: [
    'Ich komme aus dem Banking. Jahrelang habe ich Kunden beraten – und dabei gesehen, wie Banken wirklich Geld verdienen: nicht mit Prognosen, sondern mit Wahrscheinlichkeiten.',
    'Seit 2019 handle ich hauptberuflich. Nicht als Hobby, nicht nebenbei – börsentäglich, mit eigenem Kapital und festem Prozess.',
    'Genau diesen Prozess siehst du hier im Campus. Nachvollziehbar, transparent, mit allem, was dazugehört – auch den Trades, die nicht aufgehen.',
  ],
  imageSrc: svenBolz,
  imageAlt: 'Sven Bolz',
};

export const DEFAULT_CONTENT: LandingPageContent = {
  hero: DEFAULT_HERO,
  benefits: DEFAULT_BENEFITS,
  approach: DEFAULT_APPROACH,
  whatYouGet: DEFAULT_WHAT_YOU_GET,
  klartext: DEFAULT_KLARTEXT,
  unterschied: DEFAULT_UNTERSCHIED,
  pricingIntro: DEFAULT_PRICING_INTRO,
  goDeeper: DEFAULT_GO_DEEPER,
  faqItems: DEFAULT_FAQ_ITEMS,
  senjaWidgetId: DEFAULT_SENJA_WIDGET_ID,
  heroVideoUrl: DEFAULT_HERO_VIDEO_URL,
  hideCampusResults: false,
  calculatorDefaultCapital: 30_000,
  calculatorNote: null,
  tradeTableNote: null,
  hideTestimonials: false,
  testimonialsHeadline: 'Das sagen unsere Mitglieder',
  testimonialsSubline: 'Echte Bewertungen von echten Tradern',
  faqCtaLabel: 'Ich will aufhören zu raten',
  closing: DEFAULT_CLOSING,
  whoTrades: DEFAULT_WHO_TRADES,
};
